// eslint-disable-next-line import/no-unresolved
import { P } from '@ui-kit/ui-kit';
import { groupBy, get, pick } from 'lodash-es';

export interface AppNotificationOptions {
  action?: 'routerLink' | 'href' | 'none';
  actionPath?: string;
  actionLabel?: string;
  dismissLabel?: string;
  dismissible?: boolean;
  dismissTime?: number;
}

export interface AppNotification {
  id: string;
  body: string;
  badge?: string;
  badgeTheme?: string;
  date?: string;
  icon?: string;
  options?: AppNotificationOptions;
  alert?: P.Alert | Partial<P.Alert>;
}

export interface NotificationStacks {
  stacks: NotificationStack[];
  unread: number;
  index?: number;
}

export interface NotificationStack {
  type: string;
  label?: string;
  notifications: AppNotification[];
  unread: number;
  index: number;
}

export const appNotificationDefaultOptions: AppNotificationOptions = {
  action: 'none',
  actionPath: null,
  actionLabel: null,
  dismissLabel: 'Dismiss',
  dismissible: true,
  dismissTime: -1,
};

/**
 *
 */
export function uuid(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
}

/**
 * map alert to notification
 */
export function mapAlertTypeToNotificationBadge(type: string): Partial<AppNotification> {
  switch (type) {
    case 'Eligible':
    case 'AutoApproved':
      return {
        badge: 'Eligible',
        badgeTheme: 'primary',
      };
    case 'Rejected':
    case 'AutoRejected':
      return {
        badge: 'Not eligible',
        badgeTheme: 'warn',
      };
    default:
      return {
        badge: type,
      };
  }
}
/**
 *
 */
export function mapAlertTypeToNotificationAction(alert: P.Alert | Partial<P.Alert>): Partial<AppNotificationOptions> {
  switch (alert.type) {
    case 'Eligible':
    case 'AutoApproved':
      return {
        action: 'routerLink',
        actionLabel: 'Find out more',
        actionPath: `/favourites/${alert?.probe?.id}`,
      };
    case 'Rejected':
    case 'AutoRejected':
      return {
        // action: 'routerLink',
        // actionLabel: 'Find out why',
        // actionPath: `/favourites/${alert.probe.id}`
      };
    default:
      return {};
  }
}

/**
 *
 */
export function mapAlertToNotification(
  alert: P.Alert,
  options?: AppNotificationOptions,
): AppNotification {
  // uuid for notification
  const id = uuid();

  // keys used from original payload
  const leanAlert: Partial<P.Alert> = pick(alert, [
    'id',
    'type',
    'title',
    'createdAt',
    'title',
    'probe.id',
    'probe.listing',
  ]);

  const date = leanAlert?.createdAt || new Date().toISOString();
  const body = leanAlert?.title;

  return {
    id,
    date,
    body,
    ...mapAlertTypeToNotificationBadge(leanAlert.type),
    icon: 'https://groupdigital-mccarthyandstone-co-uk.vr-360-tour.com/u/01ecfabe-aef4-11ea-b9f7-569c76300f6d/m/thumbnail.jpg',
    options: {
      ...appNotificationDefaultOptions,
      ...mapAlertTypeToNotificationAction(leanAlert),
      ...options,
    },
    alert: leanAlert,
  };
}

/**
 * sort by most recent first
 */
export function sortByDate(a: AppNotification, b: AppNotification): number {
  return new Date(b?.date).getTime() - new Date(a?.date).getTime();
}

/**
 * convert alerts to notification stacks
 */
export function mapAlertsToNotificationStacks(alerts: P.Alert[] | Partial<P.Alert[]>): NotificationStacks {
  // get unread alerts
  const unreadAlerts = alerts
    .filter((alert) => !alert.readAt)
    .map((alert) => {
      if (alert.type === 'AutoApproved') {
        alert.type = 'Eligible';
      }
      return alert;
    });

  // number of unread alerts
  const unread = unreadAlerts.length;

  // group unread alerts by type
  const groupedUnreadAlerts = groupBy(unreadAlerts, 'type');

  // map to stack
  const stacks = Object.keys(groupedUnreadAlerts)
    .map((type, index) => ({
      type,
      index,
      unread: (groupedUnreadAlerts[type] || []).length,
      notifications: (groupedUnreadAlerts[type] || [])
        .map((alert) => mapAlertToNotification(alert))
        // sort notifications in stack by date
        .sort(sortByDate),
    }))
    // sort stacks by date
    .sort((a, b) => sortByDate(get(a, 'notifications.0'), get(b, 'notifications.0')));

  return {
    stacks,
    unread,
  };
}
